// src/pages/emailMarketing/CampaignsListPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import emailMarketingService from '../../helpers/emailMarketingService';
import Swal from 'sweetalert2';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaChartBar, FaEye, FaPaperPlane } from 'react-icons/fa';

function CampaignsListPage() {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [marcas, setMarcas] = useState([]);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [selectedMarcaData, setSelectedMarcaData] = useState(null); // Datos completos de la marca seleccionada
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchMarcas();
    }, [user]);

    useEffect(() => {
        if (selectedMarca) {
            fetchMarcaData();
            fetchCampaigns();
        }
    }, [selectedMarca, statusFilter, searchTerm]);

    const fetchMarcas = async () => {
        try {
            if (user?.userId) {
                const response = await get(`marcas/user/${user.userId}`);
                setMarcas(response);
                if (response.length > 0) {
                    setSelectedMarca(response[0]._id);
                }
            }
        } catch (error) {
            // Error silencioso
        }
    };

    const fetchMarcaData = async () => {
        if (!selectedMarca) return;
        try {
            const marcaData = await get(`marcas/${selectedMarca}`);
            setSelectedMarcaData(marcaData);
        } catch (error) {
            // Error silencioso
        }
    };

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const filters = {
                limit: 50
            };
            if (statusFilter !== 'all') {
                filters.status = statusFilter;
            }
            if (searchTerm) {
                filters.search = searchTerm;
            }

            const response = await emailMarketingService.getCampaigns(selectedMarca, filters);
            const campaignsList = response.data || response || [];
            setCampaigns(campaignsList);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (campaignId) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await emailMarketingService.deleteCampaign(campaignId);
                Swal.fire('Eliminado', 'La campaña ha sido eliminada correctamente', 'success');
                fetchCampaigns();
            } catch (error) {
                Swal.fire('Error', error.message, 'error');
            }
        }
    };

    const handleSend = async (campaign) => {
        // Validar que la campaña esté en draft
        if (campaign.status !== 'draft') {
            Swal.fire('Error', 'Solo se pueden enviar campañas en estado borrador', 'error');
            return;
        }

        // Validar que tenga destinatarios
        if (!campaign.totalRecipients || campaign.totalRecipients === 0) {
            Swal.fire('Error', 'La campaña no tiene destinatarios. Verifica la segmentación.', 'error');
            return;
        }

        // Verificar si el envío está bloqueado
        if (selectedMarcaData?.emailSendingBlocked) {
            const reason = selectedMarcaData.emailSendingBlockedReason || 'tasa de quejas alta';
            Swal.fire({
                title: 'Envío bloqueado',
                html: `
                    <p>El envío de emails está bloqueado para esta marca.</p>
                    <p class="text-sm mt-2"><strong>Razón:</strong> ${reason}</p>
                    ${selectedMarcaData.emailSendingBlockedAt ? 
                        `<p class="text-xs mt-1 text-gray-600">Bloqueado desde: ${new Date(selectedMarcaData.emailSendingBlockedAt).toLocaleDateString()}</p>` 
                        : ''}
                    <p class="text-sm mt-3">Contacta con soporte para más información.</p>
                `,
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return;
        }

        const result = await Swal.fire({
            title: '¿Enviar campaña?',
            html: `
                <p>Se enviará la campaña <strong>${campaign.name}</strong></p>
                <p>Total de destinatarios: <strong>${campaign.totalRecipients}</strong></p>
                <p class="text-sm text-gray-600 mt-2">Los emails se procesarán en batches con rate limiting automático.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Sí, enviar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                // Preparar campaña para envío
                await emailMarketingService.prepareCampaign(campaign._id);
                
                // Procesar primer batch
                await emailMarketingService.processCampaign(campaign._id, 50);
                
                Swal.fire({
                    title: 'Campaña iniciada',
                    html: `
                        <p>La campaña ha comenzado a enviarse.</p>
                        <p class="text-sm mt-2">Se procesarán aproximadamente 60 emails por minuto.</p>
                    `,
                    icon: 'success',
                    confirmButtonText: 'Ver Progreso'
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate(`/email-marketing/campaigns/${campaign._id}/progress`);
                    } else {
                        fetchCampaigns();
                    }
                });
            } catch (error) {
                Swal.fire('Error', error.message, 'error');
            }
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            draft: { color: 'bg-gray-500', text: 'Borrador' },
            scheduled: { color: 'bg-blue-500', text: 'Programada' },
            sending: { color: 'bg-yellow-500', text: 'Enviando' },
            sent: { color: 'bg-green-500', text: 'Enviada' },
            paused: { color: 'bg-orange-500', text: 'Pausada' },
            failed: { color: 'bg-red-500', text: 'Fallida' }
        };
        const badge = badges[status] || badges.draft;
        return (
            <span className={`px-2 py-1 rounded text-white text-xs ${badge.color}`}>
                {badge.text}
            </span>
        );
    };

    if (loading && campaigns.length === 0) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">Cargando...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Advertencia de bloqueo de envío */}
            {selectedMarcaData?.emailSendingBlocked && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3 flex-1">
                            <h3 className="text-sm font-medium text-red-800">
                                Envío de emails bloqueado
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>
                                    El envío de emails está bloqueado para esta marca.
                                    <strong> Razón:</strong> {selectedMarcaData.emailSendingBlockedReason || 'Tasa de quejas alta'}
                                </p>
                                {selectedMarcaData.emailSendingBlockedAt && (
                                    <p className="mt-1 text-xs">
                                        Bloqueado desde: {new Date(selectedMarcaData.emailSendingBlockedAt).toLocaleDateString()}
                                    </p>
                                )}
                                <p className="mt-2">
                                    No se podrán enviar campañas hasta que se resuelva el bloqueo. 
                                    Contacta con soporte para más información.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Campañas de Email Marketing</h1>
                <button
                    onClick={() => navigate('/email-marketing/campaigns/new')}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
                >
                    <FaPlus className="mr-2" />
                    Nueva Campaña
                </button>
            </div>

            {/* Filtros */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {marcas.length > 0 && (
                        <select
                            value={selectedMarca || ''}
                            onChange={(e) => setSelectedMarca(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded"
                        >
                            {marcas.map(marca => (
                                <option key={marca._id} value={marca._id}>
                                    {marca.nombre}
                                </option>
                            ))}
                        </select>
                    )}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded"
                    >
                        <option value="all">Todos los estados</option>
                        <option value="draft">Borrador</option>
                        <option value="scheduled">Programada</option>
                        <option value="sending">Enviando</option>
                        <option value="sent">Enviada</option>
                        <option value="paused">Pausada</option>
                        <option value="failed">Fallida</option>
                    </select>
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-3 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar campaña..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded"
                        />
                    </div>
                </div>
            </div>

            {/* Listado de campañas */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {campaigns.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No hay campañas. Crea tu primera campaña para comenzar.
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asunto</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatarios</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enviados</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {campaigns.map((campaign) => (
                                <tr key={campaign._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-500">{campaign.subject}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(campaign.status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {campaign.totalRecipients || 0}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {campaign.sentCount || 0}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            {campaign.status === 'draft' && (
                                                <button
                                                    onClick={() => handleSend(campaign)}
                                                    disabled={selectedMarcaData?.emailSendingBlocked}
                                                    className={`${selectedMarcaData?.emailSendingBlocked ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:text-green-900'}`}
                                                    title={selectedMarcaData?.emailSendingBlocked ? 'Envío bloqueado' : 'Enviar campaña'}
                                                >
                                                    <FaPaperPlane />
                                                </button>
                                            )}
                                            {(campaign.status === 'sending' || campaign.status === 'sent') && (
                                                <button
                                                    onClick={() => navigate(`/email-marketing/campaigns/${campaign._id}/stats`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Ver estadísticas"
                                                >
                                                    <FaChartBar />
                                                </button>
                                            )}
                                            {campaign.status === 'sending' && (
                                                <button
                                                    onClick={() => navigate(`/email-marketing/campaigns/${campaign._id}/progress`)}
                                                    className="text-yellow-600 hover:text-yellow-900"
                                                    title="Ver progreso"
                                                >
                                                    <FaEye />
                                                </button>
                                            )}
                                            {campaign.status === 'draft' && (
                                                <button
                                                    onClick={() => navigate(`/email-marketing/campaigns/${campaign._id}/edit`)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="Editar"
                                                >
                                                    <FaEdit />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(campaign._id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default CampaignsListPage;

